/**
 * SegmentedButton color — appearance × intent.
 * Density lives in SegmentedButton.size.ts.
 *
 * Intent names match Button exactly. Tokens are Invirtus CSS only.
 * Rule: icon ink matches label ink in the same state.
 *
 * Active surfaces split into surface + ink so the sliding indicator can
 * animate the fill while label/icon keep the correct contrast ink.
 */

import { cn } from '@/utils/cn';
import type {
  SegmentedButtonAppearance,
  SegmentedButtonIntent,
} from './SegmentedButton.types';

export interface SegmentedIntentSurface {
  /** Soft track behind segments — used for appearance=raised only. */
  track: string;
  /** Active fill when appearance=filled (no text). */
  filledActiveSurface: string;
  /** Active label/icon ink when appearance=filled. */
  filledActiveInk: string;
  /** Active fill when appearance=raised (no text). */
  raisedActiveSurface: string;
  /** Active label/icon ink when appearance=raised. */
  raisedActiveInk: string;
  /** Inactive + hover — stays in the intent family. */
  inactive: string;
  focus: string;
}

/**
 * Resolved surfaces per Button intent.
 * filled ≈ Button contained on shared gray track; raised ≈ light pill on intent-tinted track.
 * `track` is used only for appearance=raised.
 */
export const SEGMENTED_INTENT: Record<SegmentedButtonIntent, SegmentedIntentSurface> = {
  primary: {
    track: 'bg-purple/30 border border-purple/25',
    filledActiveSurface: 'bg-purple shadow-sm',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-purple-soft/40',
    focus: 'focus-visible:ring-purple/40',
  },
  secondary: {
    track: 'bg-background-gray border border-light-lila/40',
    filledActiveSurface: 'bg-dark-invirtus shadow-sm',
    filledActiveInk: 'text-light',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-dark-invirtus/10',
    focus: 'focus-visible:ring-dark-invirtus/30',
  },
  light: {
    track: 'bg-background-gray border border-light-lila/40',
    filledActiveSurface: 'bg-light shadow-sm ring-1 ring-light-lila/50',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-light/80',
    focus: 'focus-visible:ring-dark-invirtus/20',
  },
  success: {
    track: 'bg-green/30 border border-green/25',
    filledActiveSurface: 'bg-green shadow-sm',
    filledActiveInk: 'text-light',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-green/20',
    focus: 'focus-visible:ring-green/40',
  },
  danger: {
    track: 'bg-red/30 border border-red/25',
    filledActiveSurface: 'bg-red shadow-sm',
    filledActiveInk: 'text-light',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-red/20',
    focus: 'focus-visible:ring-red/40',
  },
  warning: {
    track: 'bg-yellow/30 border border-yellow/30',
    filledActiveSurface: 'bg-yellow shadow-sm',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-yellow/30',
    focus: 'focus-visible:ring-yellow/40',
  },
  info: {
    track: 'bg-blue-soft/60 border border-blue/30',
    filledActiveSurface: 'bg-blue-soft shadow-sm',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-blue-soft/40',
    focus: 'focus-visible:ring-blue/40',
  },
  muted: {
    track: 'bg-background-gray border border-dark-invirtus-soft',
    filledActiveSurface: 'bg-dark-invirtus-soft shadow-sm',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus/80',
    inactive: 'text-dark-invirtus/50 hover:text-dark-invirtus/70 hover:bg-dark-invirtus-soft/50',
    focus: 'focus-visible:ring-dark-invirtus/20',
  },
  'success-soft': {
    track: 'bg-green-soft/40 border border-green/20',
    filledActiveSurface: 'bg-green-soft shadow-sm',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-green-soft/50',
    focus: 'focus-visible:ring-green/30',
  },
  'danger-soft': {
    track: 'bg-red-soft/40 border border-red/20',
    filledActiveSurface: 'bg-red-soft shadow-sm',
    filledActiveInk: 'text-dark-invirtus',
    raisedActiveSurface: 'bg-light shadow-sm',
    raisedActiveInk: 'text-dark-invirtus',
    inactive: 'text-dark-invirtus/55 hover:text-dark-invirtus hover:bg-red-soft/50',
    focus: 'focus-visible:ring-red/30',
  },
};

/**
 * Shared track for appearance=filled — same light gray for every intent.
 * Active segment carries the intent color; track stays neutral.
 */
export const SEGMENTED_FILLED_TRACK = 'bg-background-gray/50 border border-light-lila/40';

export function getSegmentedTrackSurface(
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
): string {
  if (appearance === 'filled') return SEGMENTED_FILLED_TRACK;
  return SEGMENTED_INTENT[intent].track;
}

export function getSegmentedActiveSurface(
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
): string {
  const surface = SEGMENTED_INTENT[intent];
  return appearance === 'filled' ? surface.filledActiveSurface : surface.raisedActiveSurface;
}

export function getSegmentedActiveInk(
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
): string {
  const surface = SEGMENTED_INTENT[intent];
  return appearance === 'filled' ? surface.filledActiveInk : surface.raisedActiveInk;
}

export function getSegmentedActiveClass(
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
): string {
  return cn(getSegmentedActiveSurface(appearance, intent), getSegmentedActiveInk(appearance, intent));
}

export function getSegmentedInactiveClass(intent: SegmentedButtonIntent): string {
  return SEGMENTED_INTENT[intent].inactive;
}

export function getSegmentedFocusClass(intent: SegmentedButtonIntent): string {
  return SEGMENTED_INTENT[intent].focus;
}
